import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-violet-600 flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <span className="text-zinc-900 text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            DocuBot AI
          </span>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
          {submitted ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Check your inbox
              </h2>
              <p className="text-zinc-600 text-sm leading-relaxed">
                If an account exists for <span className="font-medium text-zinc-900">{email}</span>, we've sent a link to reset your password. The link expires in 30 minutes.
              </p>
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-700 font-medium mt-2"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </Link>
            </div>
          ) : (
            <>
              <div className="mx-auto h-14 w-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
                <Mail className="h-7 w-7 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Forgot your password?
              </h2>
              <p className="text-zinc-600 text-sm text-center mb-6">
                Enter your email and we'll send you a link to reset it.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="mt-2"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-zinc-900 hover:bg-zinc-800"
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
                  data-testid="forgot-password-back-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
